// src/app/core/pages/HospitalProfilePage.jsx
import React from "react";
import { useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Divider,
  Typography,
  Chip,
  Stack,
  Card,
  CardContent,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import PageShell from "../components/PageShell";
import EntityTable from "../components/EntityTable";
import EntityDialog from "../components/EntityDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import WardRoomBedMatrix from "../lists/WardRoomBedMatrix";

import {
  listHospitals,
  updateHospital,
  deleteHospital,
  listWards,
  createWard,
  updateWard,
  deleteWard,
  listRooms,
  createRoom,
  updateRoom,
  deleteRoom,
  listBeds,
  createBed,
  updateBed,
  deleteBed,
  listDepartments,
  createDepartment,
  updateDepartment,
  deleteDepartment,
  getUsers,
  createUser,
  updateUser,
  deleteUser,
} from "../api/coreApi";

import { fkId, normalizeList, sameId } from "../utils/coreHelpers";
import { Colors } from "../../utils/Colors";

/** reusable theme styles */
const themedContainedBtn = {
  backgroundColor: Colors.brand.primary,
  color: "#fff",
  borderRadius: 2,
  fontWeight: 700,
  textTransform: "none",
  boxShadow: Colors.shadow.brand,
  "&:hover": {
    backgroundColor: Colors.brand.primaryDark,
  },
};

const themedOutlinedBtn = {
  color: Colors.brand.primary,
  borderColor: Colors.brand.primary,
  borderRadius: 2,
  fontWeight: 700,
  textTransform: "none",
  "&:hover": {
    borderColor: Colors.brand.primaryDark,
    backgroundColor: Colors.bg.hover,
  },
};

const dangerContainedBtn = {
  backgroundColor: Colors.status.danger,
  color: "#fff",
  borderRadius: 2,
  fontWeight: 700,
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#dc2626",
  },
};

const softCardSx = {
  borderRadius: 3,
  border: `1px solid ${Colors.border.light}`,
  backgroundColor: Colors.bg.paper,
  boxShadow: "0 6px 20px rgba(11,60,63,0.08)",
};

const softAlertErrorSx = {
  mb: 2,
  borderRadius: 2,
  border: `1px solid rgba(239, 68, 68, 0.18)`,
  backgroundColor: "#fff5f5",
  color: Colors.status.danger,
  "& .MuiAlert-icon": {
    color: Colors.status.danger,
  },
};

const softAlertInfoSx = {
  borderRadius: 2,
  border: `1px solid rgba(59, 130, 246, 0.16)`,
  backgroundColor: "#f8fbff",
  color: Colors.status.info,
  "& .MuiAlert-icon": {
    color: Colors.status.info,
  },
};

const softAlertWarnSx = {
  borderRadius: 2,
  border: `1px solid rgba(245, 158, 11, 0.18)`,
  backgroundColor: "#fffaf0",
  color: Colors.status.warning,
  "& .MuiAlert-icon": {
    color: Colors.status.warning,
  },
};

/** ✅ Mobile-first small-screen list renderer */
function MobileEntityList({
  title,
  rows,
  getRowKey,
  renderPrimary,
  renderMeta,
  onEdit,
  onDelete,
  emptyText = "No data",
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          fontWeight: 800,
          mb: 1,
          color: Colors.text.title,
          fontSize: 18,
        }}
      >
        {title}
      </Typography>

      {!rows?.length ? (
        <Alert severity="info" sx={softAlertInfoSx}>
          {emptyText}
        </Alert>
      ) : (
        <Stack spacing={1}>
          {rows.map((r) => {
            const key = getRowKey(r);
            const meta = (renderMeta?.(r) || []).filter(Boolean);

            return (
              <Card key={key} variant="outlined" sx={softCardSx}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      mb: 0.75,
                      lineHeight: 1.2,
                      color: Colors.text.header,
                    }}
                  >
                    {renderPrimary(r)}
                  </Typography>

                  {!!meta.length && (
                    <Stack spacing={0.35} sx={{ mb: 1 }}>
                      {meta.map((m, idx) => (
                        <Box
                          key={idx}
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "space-between",
                            alignItems: "baseline",
                          }}
                        >
                          <Typography
                            sx={{
                              color: Colors.text.muted,
                              fontSize: 12,
                            }}
                          >
                            {m.label}
                          </Typography>
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: 12,
                              textAlign: "right",
                              color: Colors.text.body,
                            }}
                          >
                            {String(m.value ?? "-")}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  )}

                  <Stack direction="column" spacing={1}>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={() => onEdit(r)}
                      sx={themedOutlinedBtn}
                    >
                      Edit
                    </Button>
                    <Button
                      fullWidth
                      size="small"
                      variant="contained"
                      onClick={() => onDelete(r)}
                      sx={dangerContainedBtn}
                    >
                      Delete
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

export default function HospitalProfilePage() {
  const { id } = useParams();
  const hospitalId = String(id);

  const theme = useTheme();

  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const isXsTight = useMediaQuery("(max-width:360px)");
  const isUltraTight = useMediaQuery("(max-width:320px)");

  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState("");

  const [hospitals, setHospitals] = React.useState([]);
  const [wards, setWards] = React.useState([]);
  const [rooms, setRooms] = React.useState([]);
  const [beds, setBeds] = React.useState([]);
  const [departments, setDepartments] = React.useState([]);
  const [users, setUsers] = React.useState([]);

  const [dlg, setDlg] = React.useState({ open: false, kind: "", mode: "add", row: null });
  const closeDlg = () => setDlg({ open: false, kind: "", mode: "add", row: null });

  const [confirm, setConfirm] = React.useState({ open: false, kind: "", row: null });
  const closeConfirm = () => setConfirm({ open: false, kind: "", row: null });

  const refreshAll = async () => {
    setErr("");
    try {
      const [hs, ws, rs, bs, ds, us] = await Promise.all([
        listHospitals(),
        listWards(),
        listRooms(),
        listBeds(),
        listDepartments(),
        getUsers(),
      ]);

      setHospitals(normalizeList(hs.data));
      setWards(normalizeList(ws.data));
      setRooms(normalizeList(rs.data));
      setBeds(normalizeList(bs.data));
      setDepartments(normalizeList(ds.data));
      setUsers(normalizeList(us.data));
    } catch (e) {
      setErr(e?.response?.data ? JSON.stringify(e.response.data) : "Failed to load profile data");
    }
  };

  React.useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hospitalId]);

  const hospital = hospitals.find((h) => sameId(fkId(h.hospital_id || h.id), hospitalId));

  const wardsOfHospital = wards.filter((w) =>
    sameId(fkId(w.organization || w.hospital || w.organization_id), hospitalId)
  );

  const roomsOfHospital = rooms.filter((r) => {
    const wid = fkId(r.ward || r.ward_id);
    const wObj = wards.find((w) => sameId(fkId(w.ward_id || w.id), wid));
    const orgId = fkId(wObj?.organization || wObj?.hospital || wObj?.organization_id);
    return sameId(orgId, hospitalId);
  });

  const bedsOfHospital = beds.filter((b) => {
    const wid = fkId(b.ward || b.ward_id);
    const wObj = wards.find((w) => sameId(fkId(w.ward_id || w.id), wid));
    const orgId = fkId(wObj?.organization || wObj?.hospital || wObj?.organization_id);
    return sameId(orgId, hospitalId);
  });

  const departmentsOfHospital = departments.filter((d) =>
    sameId(fkId(d.organization || d.hospital || d.organization_id), hospitalId)
  );

  const usersOfHospital = users.filter((u) =>
    sameId(fkId(u.organization || u.hospital || u.organization_id), hospitalId)
  );

  const wardName = (id) =>
    wards.find((w) => sameId(fkId(w.ward_id || w.id), fkId(id)))?.name || "-";

  const roomLabel = (id) =>
    rooms.find((r) => sameId(fkId(r.room_id || r.id), fkId(id)))?.room_label || "-";

  const wardOptions = wardsOfHospital.map((w) => ({
    value: fkId(w.ward_id || w.id),
    label: `${w.name} (${w.ward_type || "-"})`,
  }));

  const roomOptionsByWard = (wardId) =>
    roomsOfHospital
      .filter((r) => sameId(fkId(r.ward || r.ward_id), fkId(wardId)))
      .map((r) => ({
        value: fkId(r.room_id || r.id),
        label: `Room ${r.room_label}`,
      }));

  const openAdd = (kind) => setDlg({ open: true, kind, mode: "add", row: null });
  const openEdit = (kind, row) => setDlg({ open: true, kind, mode: "edit", row });
  const openDelete = (kind, row) => setConfirm({ open: true, kind, row });

  const submitHospital = async (vals) => {
    setLoading(true);
    setErr("");
    try {
      await updateHospital(hospitalId, vals);
      await refreshAll();
      closeDlg();
    } catch (e) {
      setErr(e?.response?.data ? JSON.stringify(e.response.data) : "Update hospital failed");
    } finally {
      setLoading(false);
    }
  };

  const submitWard = async (vals) => {
    setLoading(true);
    setErr("");
    try {
      if (dlg.mode === "add") {
        await createWard({ ...vals, organization: hospitalId });
      } else {
        const wid = fkId(dlg.row?.ward_id || dlg.row?.id);
        await updateWard(wid, { ...vals, organization: hospitalId });
      }
      await refreshAll();
      closeDlg();
    } catch (e) {
      setErr(e?.response?.data ? JSON.stringify(e.response.data) : "Ward save failed");
    } finally {
      setLoading(false);
    }
  };

  const submitRoom = async (vals) => {
    setLoading(true);
    setErr("");
    try {
      if (dlg.mode === "add") await createRoom(vals);
      else {
        const rid = fkId(dlg.row?.room_id || dlg.row?.id);
        await updateRoom(rid, vals);
      }
      await refreshAll();
      closeDlg();
    } catch (e) {
      setErr(e?.response?.data ? JSON.stringify(e.response.data) : "Room save failed");
    } finally {
      setLoading(false);
    }
  };

  const submitBed = async (vals) => {
    setLoading(true);
    setErr("");
    try {
      if (dlg.mode === "add") await createBed(vals);
      else {
        const bid = fkId(dlg.row?.bed_id || dlg.row?.id);
        await updateBed(bid, vals);
      }
      await refreshAll();
      closeDlg();
    } catch (e) {
      setErr(e?.response?.data ? JSON.stringify(e.response.data) : "Bed save failed");
    } finally {
      setLoading(false);
    }
  };

  const submitDepartment = async (vals) => {
    setLoading(true);
    setErr("");
    try {
      if (dlg.mode === "add") await createDepartment({ ...vals, organization: hospitalId });
      else {
        const did = fkId(dlg.row?.department_id || dlg.row?.id);
        await updateDepartment(did, { ...vals, organization: hospitalId });
      }
      await refreshAll();
      closeDlg();
    } catch (e) {
      setErr(e?.response?.data ? JSON.stringify(e.response.data) : "Department save failed");
    } finally {
      setLoading(false);
    }
  };

  const submitUser = async (vals) => {
    setLoading(true);
    setErr("");
    try {
      if (dlg.mode === "add") await createUser({ ...vals, organization: hospitalId });
      else {
        const uid = fkId(dlg.row?.user_id || dlg.row?.id);
        await updateUser(uid, { ...vals, organization: hospitalId });
      }
      await refreshAll();
      closeDlg();
    } catch (e) {
      setErr(e?.response?.data ? JSON.stringify(e.response.data) : "User save failed");
    } finally {
      setLoading(false);
    }
  };

  const doDelete = async () => {
    setLoading(true);
    setErr("");
    try {
      const { kind, row } = confirm;

      if (kind === "hospital") await deleteHospital(hospitalId);
      if (kind === "ward") await deleteWard(fkId(row?.ward_id || row?.id));
      if (kind === "room") await deleteRoom(fkId(row?.room_id || row?.id));
      if (kind === "bed") await deleteBed(fkId(row?.bed_id || row?.id));
      if (kind === "department") await deleteDepartment(fkId(row?.department_id || row?.id));
      if (kind === "user") await deleteUser(fkId(row?.user_id || row?.id));

      await refreshAll();
      closeConfirm();
    } catch (e) {
      setErr(e?.response?.data ? JSON.stringify(e.response.data) : "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const dialogMap = {
    hospital: {
      title: "Edit Hospital",
      submit: submitHospital,
      initialValues: {
        name: hospital?.name || "",
        registration_no: hospital?.registration_no || "",
        phone: hospital?.phone || "",
        address_line1: hospital?.address_line1 || "",
        address_line2: hospital?.address_line2 || "",
        city: hospital?.city || "",
        state: hospital?.state || "",
        country: hospital?.country || "India",
        postal_code: hospital?.postal_code || "",
        status: hospital?.status || "ACTIVE",
      },
      fields: [
        { name: "name", label: "Hospital Name", required: true },
        { name: "registration_no", label: "Registration No", required: true },
        { name: "phone", label: "Phone", required: true, validation: "phone" },
        { name: "address_line1", label: "Address line 1", required: true, fullRow: true },
        { name: "address_line2", label: "Address line 2", required: true, fullRow: true },
        { name: "city", label: "City", required: true },
        { name: "state", label: "State", required: true },
        { name: "country", label: "Country", required: true },
        { name: "postal_code", label: "Postal Code", required: true },
        {
          name: "status",
          label: "Status",
          required: true,
          type: "select",
          options: [
            { value: "ACTIVE", label: "Active" },
            { value: "SUSPENDED", label: "Suspended" },
          ],
        },
      ],
    },

    ward: {
      title: dlg.mode === "add" ? "Add Ward" : "Edit Ward",
      submit: submitWard,
      initialValues: {
        name: dlg.row?.name || "",
        ward_type: dlg.row?.ward_type || "GENERAL",
        floor: dlg.row?.floor || "",
      },
      fields: [
        { name: "name", label: "Ward Name", required: true },
        {
          name: "ward_type",
          label: "Ward Type",
          required: true,
          type: "select",
          options: [
            { value: "ICU", label: "ICU" },
            { value: "EMERGENCY", label: "Emergency" },
            { value: "GENERAL", label: "General" },
            { value: "OT", label: "OT" },
            { value: "OTHER", label: "Other" },
          ],
        },
        { name: "floor", label: "Floor", required: true },
      ],
    },

    room: {
      title: dlg.mode === "add" ? "Add Room" : "Edit Room",
      submit: submitRoom,
      initialValues: {
        ward: fkId(dlg.row?.ward || dlg.row?.ward_id) || wardOptions[0]?.value || "",
        room_label: dlg.row?.room_label || "",
        room_type: dlg.row?.room_type || "",
      },
      fields: [
        { name: "ward", label: "Ward", required: true, type: "select", options: () => wardOptions },
        { name: "room_label", label: "Room Label", required: true },
        { name: "room_type", label: "Room Type", required: true },
      ],
    },

    bed: {
      title: dlg.mode === "add" ? "Add Bed" : "Edit Bed",
      submit: submitBed,
      initialValues: {
        ward: fkId(dlg.row?.ward || dlg.row?.ward_id) || wardOptions[0]?.value || "",
        room: fkId(dlg.row?.room || dlg.row?.room_id) || "",
        bed_label: dlg.row?.bed_label || "",
        is_active: dlg.row?.is_active ?? true,
      },
      fields: [
        { name: "ward", label: "Ward", required: true, type: "select", options: () => wardOptions },
        { name: "room", label: "Room", required: true, type: "select", options: (vals) => roomOptionsByWard(vals.ward) },
        { name: "bed_label", label: "Bed Label", required: true },
        {
          name: "is_active",
          label: "Active",
          required: true,
          type: "select",
          options: [
            { value: true, label: "True" },
            { value: false, label: "False" },
          ],
        },
      ],
    },

    department: {
      title: dlg.mode === "add" ? "Add Department" : "Edit Department",
      submit: submitDepartment,
      initialValues: {
        name: dlg.row?.name || "",
        code: dlg.row?.code || "",
        is_active: dlg.row?.is_active ?? true,
      },
      fields: [
        { name: "name", label: "Department Name", required: true },
        { name: "code", label: "Code", required: true },
        {
          name: "is_active",
          label: "Active",
          required: true,
          type: "select",
          options: [
            { value: true, label: "True" },
            { value: false, label: "False" },
          ],
        },
      ],
    },

    user: {
      title: dlg.mode === "add" ? "Add User/Staff" : "Edit User/Staff",
      submit: submitUser,
      initialValues: {
        full_name: dlg.row?.full_name || "",
        email: dlg.row?.email || "",
        phone: dlg.row?.phone || "",
        status: dlg.row?.status || "INVITED",
      },
      fields: [
        { name: "full_name", label: "Full Name", required: true },
        { name: "email", label: "Email", required: true, validation: "email" },
        { name: "phone", label: "Phone", required: true, validation: "phone" },
        {
          name: "status",
          label: "Status",
          required: true,
          type: "select",
          options: [
            { value: "ACTIVE", label: "Active" },
            { value: "INVITED", label: "Invited" },
            { value: "DISABLED", label: "Disabled" },
          ],
        },
      ],
    },
  };

  const dlgConfig = dialogMap[dlg.kind];

  const wardColumns = React.useMemo(() => {
    const cols = [
      { key: "name", label: "Ward Name" },
      { key: "ward_type", label: "Type" },
      { key: "floor", label: "Floor" },
    ];
    cols.push({
      key: "_actions",
      label: "Actions",
      render: (r) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" onClick={() => openEdit("ward", r)} sx={themedOutlinedBtn}>
            Edit
          </Button>
          <Button size="small" variant="contained" onClick={() => openDelete("ward", r)} sx={dangerContainedBtn}>
            Delete
          </Button>
        </Box>
      ),
    });
    return cols;
  }, []);

  const roomColumns = React.useMemo(() => {
    const cols = [
      { key: "room_label", label: "Room" },
      { key: "room_type", label: "Type" },
      { key: "ward", label: "Ward", render: (r) => wardName(r.ward || r.ward_id) },
    ];
    cols.push({
      key: "_actions",
      label: "Actions",
      render: (r) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" onClick={() => openEdit("room", r)} sx={themedOutlinedBtn}>
            Edit
          </Button>
          <Button size="small" variant="contained" onClick={() => openDelete("room", r)} sx={dangerContainedBtn}>
            Delete
          </Button>
        </Box>
      ),
    });
    return cols;
  }, [wards]);

  const bedColumns = React.useMemo(() => {
    const cols = [
      { key: "bed_label", label: "Bed" },
      { key: "is_active", label: "Active" },
      { key: "ward", label: "Ward", render: (r) => wardName(r.ward || r.ward_id) },
      { key: "room", label: "Room", render: (r) => (r.room ? `Room ${roomLabel(r.room)}` : "No Room") },
    ];
    cols.push({
      key: "_actions",
      label: "Actions",
      render: (r) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" onClick={() => openEdit("bed", r)} sx={themedOutlinedBtn}>
            Edit
          </Button>
          <Button size="small" variant="contained" onClick={() => openDelete("bed", r)} sx={dangerContainedBtn}>
            Delete
          </Button>
        </Box>
      ),
    });
    return cols;
  }, [wards, rooms]);

  const departmentColumns = React.useMemo(() => {
    const cols = [
      { key: "name", label: "Department" },
      { key: "code", label: "Code" },
      { key: "is_active", label: "Active" },
    ];
    cols.push({
      key: "_actions",
      label: "Actions",
      render: (r) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" onClick={() => openEdit("department", r)} sx={themedOutlinedBtn}>
            Edit
          </Button>
          <Button size="small" variant="contained" onClick={() => openDelete("department", r)} sx={dangerContainedBtn}>
            Delete
          </Button>
        </Box>
      ),
    });
    return cols;
  }, []);

  const userColumns = React.useMemo(() => {
    const cols = [
      { key: "full_name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone" },
      { key: "status", label: "Status" },
    ];
    cols.push({
      key: "_actions",
      label: "Actions",
      render: (r) => (
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button size="small" variant="outlined" onClick={() => openEdit("user", r)} sx={themedOutlinedBtn}>
            Edit
          </Button>
          <Button size="small" variant="contained" onClick={() => openDelete("user", r)} sx={dangerContainedBtn}>
            Delete
          </Button>
        </Box>
      ),
    });
    return cols;
  }, []);

  return (
    <PageShell
      title={hospital ? hospital.name : "Hospital Profile"}
      subtitle={isSmall ? "" : "Correlated Structure: Ward → Room → Bed (plus CRUD for all entities)"}
    >
      {err ? (
        <Alert severity="error" sx={softAlertErrorSx}>
          {err}
        </Alert>
      ) : null}

      {!hospital ? (
        <Alert severity="warning" sx={softAlertWarnSx}>
          Hospital not found or loading...
        </Alert>
      ) : (
        <>
          <Stack
            direction="column"
            spacing={1}
            sx={{
              mb: 2,
              p: isSmall ? 1 : 0,
              borderRadius: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 1000,
                fontSize: isUltraTight ? 16 : isSmall ? 18 : 22,
                lineHeight: 1.15,
                wordBreak: "break-word",
                color: Colors.text.title,
              }}
            >
              {hospital.name}
            </Typography>

            {!isSmall && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={hospital.city || "-"}
                  sx={{
                    backgroundColor: Colors.bg.hover,
                    color: Colors.text.body,
                    border: `1px solid ${Colors.border.light}`,
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={hospital.state || "-"}
                  sx={{
                    backgroundColor: Colors.bg.hover,
                    color: Colors.text.body,
                    border: `1px solid ${Colors.border.light}`,
                    fontWeight: 600,
                  }}
                />
                <Chip
                  label={hospital.status || "-"}
                  sx={{
                    backgroundColor:
                      hospital.status === "ACTIVE" ? "rgba(34,197,94,0.10)" : "rgba(245,158,11,0.12)",
                    color:
                      hospital.status === "ACTIVE" ? Colors.status.success : Colors.status.warning,
                    border: `1px solid ${
                      hospital.status === "ACTIVE"
                        ? "rgba(34,197,94,0.20)"
                        : "rgba(245,158,11,0.20)"
                    }`,
                    fontWeight: 700,
                  }}
                />
              </Box>
            )}

            <Stack direction={isSmall ? "column" : "row"} spacing={1}>
              <Button
                fullWidth={isSmall}
                size={isSmall ? "small" : "medium"}
                variant="outlined"
                onClick={() => openEdit("hospital", hospital)}
                sx={themedOutlinedBtn}
              >
                Edit Hospital
              </Button>
            </Stack>
          </Stack>

          {!isSmall ? (
            <Box
              sx={{
                width: "100%",
                overflowX: "auto",
                mb: 1,
                p: 1,
                borderRadius: 3,
                backgroundColor: Colors.bg.paper,
                border: `1px solid ${Colors.border.light}`,
              }}
            >
              <Box sx={{ minWidth: isXsTight ? 520 : "auto" }}>
                <WardRoomBedMatrix
                  wards={wardsOfHospital}
                  rooms={roomsOfHospital}
                  beds={bedsOfHospital}
                />
              </Box>
            </Box>
          ) : (
            <Card variant="outlined" sx={{ ...softCardSx, mb: 2 }}>
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography
                  sx={{
                    fontWeight: 900,
                    mb: 0.5,
                    color: Colors.text.header,
                  }}
                >
                  Quick Summary
                </Typography>
                <Stack spacing={0.5}>
                  <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
                    Wards: <b>{wardsOfHospital.length}</b>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
                    Rooms: <b>{roomsOfHospital.length}</b>
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: Colors.text.body }}>
                    Beds: <b>{bedsOfHospital.length}</b>
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}

          <Divider sx={{ my: 2, borderColor: Colors.border.topLine }} />

          {isSmall ? (
            <>
              <MobileEntityList
                title="Wards"
                rows={wardsOfHospital}
                getRowKey={(r) => fkId(r.ward_id || r.id)}
                renderPrimary={(r) => r.name}
                renderMeta={(r) => [
                  { label: "Type", value: r.ward_type },
                  { label: "Floor", value: r.floor },
                ]}
                onEdit={(r) => openEdit("ward", r)}
                onDelete={(r) => openDelete("ward", r)}
              />
              <Button fullWidth variant="contained" onClick={() => openAdd("ward")} sx={{ ...themedContainedBtn, mb: 2 }}>
                Add Ward
              </Button>

              <MobileEntityList
                title="Rooms"
                rows={roomsOfHospital}
                getRowKey={(r) => fkId(r.room_id || r.id)}
                renderPrimary={(r) => `Room ${r.room_label}`}
                renderMeta={(r) => [
                  { label: "Type", value: r.room_type },
                  { label: "Ward", value: wardName(r.ward || r.ward_id) },
                ]}
                onEdit={(r) => openEdit("room", r)}
                onDelete={(r) => openDelete("room", r)}
              />
              <Button fullWidth variant="contained" onClick={() => openAdd("room")} sx={{ ...themedContainedBtn, mb: 2 }}>
                Add Room
              </Button>

              <MobileEntityList
                title="Beds"
                rows={bedsOfHospital}
                getRowKey={(r) => fkId(r.bed_id || r.id)}
                renderPrimary={(r) => r.bed_label}
                renderMeta={(r) => [
                  { label: "Active", value: String(r.is_active ?? "-") },
                  { label: "Ward", value: wardName(r.ward || r.ward_id) },
                  { label: "Room", value: r.room ? `Room ${roomLabel(r.room)}` : "No Room" },
                ]}
                onEdit={(r) => openEdit("bed", r)}
                onDelete={(r) => openDelete("bed", r)}
              />
              <Button fullWidth variant="contained" onClick={() => openAdd("bed")} sx={{ ...themedContainedBtn, mb: 2 }}>
                Add Bed
              </Button>

              <MobileEntityList
                title="Departments"
                rows={departmentsOfHospital}
                getRowKey={(r) => fkId(r.department_id || r.id)}
                renderPrimary={(r) => r.name}
                renderMeta={(r) => [
                  { label: "Code", value: r.code },
                  { label: "Active", value: String(r.is_active ?? "-") },
                ]}
                onEdit={(r) => openEdit("department", r)}
                onDelete={(r) => openDelete("department", r)}
              />
              <Button fullWidth variant="contained" onClick={() => openAdd("department")} sx={{ ...themedContainedBtn, mb: 2 }}>
                Add Department
              </Button>

              <MobileEntityList
                title="Users / Staff"
                rows={usersOfHospital}
                getRowKey={(r) => fkId(r.user_id || r.id)}
                renderPrimary={(r) => r.full_name}
                renderMeta={(r) => [
                  { label: "Phone", value: r.phone },
                  { label: "Email", value: r.email },
                  { label: "Status", value: r.status },
                ]}
                onEdit={(r) => openEdit("user", r)}
                onDelete={(r) => openDelete("user", r)}
              />
              <Button fullWidth variant="contained" onClick={() => openAdd("user")} sx={{ ...themedContainedBtn, mb: 2 }}>
                Add User
              </Button>
            </>
          ) : (
            <>
              <EntityTable
                title="Wards"
                rows={wardsOfHospital}
                getRowKey={(r) => fkId(r.ward_id || r.id)}
                columns={wardColumns}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", my: 2 }}>
                <Button variant="contained" onClick={() => openAdd("ward")} sx={themedContainedBtn}>
                  Add Ward
                </Button>
              </Box>

              <EntityTable
                title="Rooms"
                rows={roomsOfHospital}
                getRowKey={(r) => fkId(r.room_id || r.id)}
                columns={roomColumns}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", my: 2 }}>
                <Button variant="contained" onClick={() => openAdd("room")} sx={themedContainedBtn}>
                  Add Room
                </Button>
              </Box>

              <EntityTable
                title="Beds"
                rows={bedsOfHospital}
                getRowKey={(r) => fkId(r.bed_id || r.id)}
                columns={bedColumns}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", my: 2 }}>
                <Button variant="contained" onClick={() => openAdd("bed")} sx={themedContainedBtn}>
                  Add Bed
                </Button>
              </Box>

              <EntityTable
                title="Departments"
                rows={departmentsOfHospital}
                getRowKey={(r) => fkId(r.department_id || r.id)}
                columns={departmentColumns}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", my: 2 }}>
                <Button variant="contained" onClick={() => openAdd("department")} sx={themedContainedBtn}>
                  Add Department
                </Button>
              </Box>

              <EntityTable
                title="Users / Staff"
                rows={usersOfHospital}
                getRowKey={(r) => fkId(r.user_id || r.id)}
                columns={userColumns}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", my: 2 }}>
                <Button variant="contained" onClick={() => openAdd("user")} sx={themedContainedBtn}>
                  Add User
                </Button>
              </Box>
            </>
          )}
        </>
      )}

      {dlgConfig ? (
        <EntityDialog
          open={dlg.open}
          title={dlgConfig.title}
          initialValues={dlgConfig.initialValues}
          fields={dlgConfig.fields}
          loading={loading}
          onClose={closeDlg}
          onSubmit={dlgConfig.submit}
        />
      ) : null}

      <ConfirmDialog
        open={confirm.open}
        title="Delete"
        description="This action cannot be undone."
        loading={loading}
        onClose={closeConfirm}
        onConfirm={doDelete}
      />
    </PageShell>
  );
}